import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { EmptyState } from '@/common/components/EmptyState/EmptyState';
import { FamilyMemberCard } from '@/common/components/FamilyMemberCard/FamilyMemberCard';
import { Grid } from '@/common/components/Grid/Grid';
import { Icon } from '@/common/components/Icons/Icon';
import { IlustrationNoFamilyMembers } from '@/common/components/Illustrations/IlustrationNoFamilyMembers';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { useModal } from '@/common/components/Modal/Modal.utils';
import { PageLayout } from '@/common/components/PageLayout/PageLayout';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { IFamilyMember } from '@/common/integrations/menhir/employee/employee/employee.types';
import { useFamilyMembers } from '@/web/hooks/useFamilyMembers';
import { FamilyMemberDrawer } from './components/FamilyMemberDrawer/FamilyMemberDrawer';
import { FamilyMemberFormModal } from './components/FamilyMemberFormModal/FamilyMemberFormModal';

export default function FamilyMembersPage() {
  const { t } = useTranslation();
  const { loading, allMembers } = useFamilyMembers();
  const { company } = useEmployeeDetails();
  const addFamilyMemberModal = useModal();
  const [selectedMember, setSelectedMember] = useState<IFamilyMember | null>(
    null
  );
  const handleClickAddMember = () => {
    AnalyticsService.trackCustomEvent('add_child_button_clicked', {
      context: 'profile_page',
    });
    addFamilyMemberModal.setIsOpen(true);
  };

  if (loading) {
    return <LoadingPlaceholder />;
  }

  return (
    <PageLayout
      headerProps={{
        primaryAction:
          allMembers && allMembers?.length > 0 ? (
            <Button
              onPress={handleClickAddMember}
              icon={<Icon icon="user-plus" />}
            >
              {t('dashboard.family_members.add_btn')}
            </Button>
          ) : undefined,
        backButton: true,
        title: t('dashboard.family_members.title'),
        description: t(
          `dashboard.family_members.description.${company.market.slug}`
        ),
      }}
    >
      <Grid gap="sibling-card" columnBasis="basis-[13rem]" maxColumns={4}>
        {allMembers?.map((child) => (
          <FamilyMemberCard
            key={child.id}
            child={child}
            onPress={() => setSelectedMember(child)}
          />
        ))}
      </Grid>
      {allMembers && allMembers?.length === 0 && (
        <EmptyState
          title={t('dashboard.family_members.without_family_members.title')}
          description={t(
            'dashboard.family_members.without_family_members.description'
          )}
          ilustration={<IlustrationNoFamilyMembers />}
          primaryButtonProps={{
            onPress: () => handleClickAddMember(),
            icon: <Icon icon="user-plus" />,
            children: t('dashboard.family_members.add_btn'),
          }}
        />
      )}
      {addFamilyMemberModal.isOpen && (
        <FamilyMemberFormModal
          onClose={() => addFamilyMemberModal.setIsOpen(false)}
        />
      )}
      {selectedMember && (
        <FamilyMemberDrawer
          onClose={() => setSelectedMember(null)}
          member={selectedMember}
        />
      )}
    </PageLayout>
  );
}
